import React, { useState, useEffect } from 'react';
import ApiClient from "../utils/ApiClient";
import PODetailModal from "../components/Purchasing/PODetailModal";

export default function Purchasing() {
    const [poData, setPoData] = useState([]);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPO, setSelectedPO] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [expandedPOs, setExpandedPOs] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [posResponse, statsResponse] = await Promise.all([
                ApiClient.get('/api/purchasing/pos'),
                ApiClient.get('/api/purchasing/dashboard')
            ]);
            setPoData(posResponse.data);
            setDashboardStats(statsResponse.data);
        } catch (error) {
            console.error('Error fetching purchasing data:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePOClick = (po) => {
        setSelectedPO(po);
        setDetailModalOpen(true);
    };

    const handleCloseModal = () => {
        setDetailModalOpen(false);
        setSelectedPO(null);
        fetchData(); // Refresh data after modal closes
    };

    const toggleExpanded = (poNumber, e) => {
        e.stopPropagation();
        setExpandedPOs(prev => ({
            ...prev,
            [poNumber]: !prev[poNumber]
        }));
    };

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    const formatDate = (value) => {
        if (!value) return '-';
        // Append T00:00:00 to parse as local time, not UTC
        const date = value.includes('T') ? new Date(value) : new Date(value + 'T00:00:00');
        return date.toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-gray-600">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-red-600">Error loading data: {error}</div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-semibold text-gray-900">Purchasing</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Manage supplier invoices, price discrepancies, and SKU mismatches
                    </p>
                </div>
            </div>

            {/* PO Summary Stats */}
            {poData && poData.length > 0 && (
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Total POs</dt>
                            <dd className="mt-1 text-3xl font-semibold text-gray-900">{poData.length}</dd>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Complete</dt>
                            <dd className="mt-1 text-3xl font-semibold text-green-600">
                                {poData.filter(po => po.reviewed).length}
                            </dd>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Has Issues</dt>
                            <dd className="mt-1 text-3xl font-semibold text-red-600">
                                {poData.filter(po => !po.reviewed).length}
                            </dd>
                        </div>
                    </div>
                </div>
            )}

            {/* Dashboard Stats */}
            {dashboardStats && (
                <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Total Invoices</dt>
                            <dd className="mt-1 text-3xl font-semibold text-gray-900">{dashboardStats.totalInvoices}</dd>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Price Issues</dt>
                            <dd className="mt-1 text-3xl font-semibold text-red-600">{dashboardStats.unresolvedPriceIssues}</dd>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">SKU Issues</dt>
                            <dd className="mt-1 text-3xl font-semibold text-yellow-600">{dashboardStats.unresolvedSkuIssues}</dd>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Pending Credits</dt>
                            <dd className="mt-1 text-3xl font-semibold text-orange-600">{dashboardStats.pendingCredits}</dd>
                        </div>
                    </div>
                </div>
            )}

            {/* PO List Table */}
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">PO #</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Qty Ord</th>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Shipped</th>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Received</th>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Damaged</th>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Missing</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">First Recv</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last Recv</th>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">PO Total</th>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Invoiced</th>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Variance</th>
                                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Price</th>
                                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">SKU</th>
                                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Status</th>
                                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Reviewed</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {poData.map((po) => (
                                        <React.Fragment key={po.poNumber}>
                                        <tr
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => handlePOClick(po)}
                                        >
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-indigo-600">
                                                <div className="flex items-center">
                                                    {(po.hasInvoices || po.hasReplacements || po.hasCredits) && (
                                                        <button
                                                            onClick={(e) => toggleExpanded(po.poNumber, e)}
                                                            className="mr-2 text-gray-400 hover:text-gray-600"
                                                        >
                                                            <svg className={`w-4 h-4 transition-transform ${expandedPOs[po.poNumber] ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {po.poNumber}
                                                    {po.hasInvoices && (
                                                        <span className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                                                            {po.invoices.length} INV
                                                        </span>
                                                    )}
                                                    {po.hasReplacements && (
                                                        <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                                            +{po.replacements.length} RE
                                                        </span>
                                                    )}
                                                    {po.hasCredits && (
                                                        <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                                            +{po.credits.length} CR
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {formatDate(po.poDate)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                                {po.qtyOrdered?.toLocaleString() || '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                                                {po.qtyShipped != null ? (
                                                    po.qtyShipped === po.qtyOrdered ? (
                                                        <span className="inline-flex items-center text-green-600">
                                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                            {po.qtyShipped.toLocaleString()}
                                                        </span>
                                                    ) : (
                                                        <span className="text-red-600 font-medium">
                                                            {po.qtyShipped.toLocaleString()}
                                                            <span className="ml-1 text-xs">
                                                                ({po.qtyShipped > po.qtyOrdered ? '+' : ''}{(po.qtyShipped - po.qtyOrdered).toLocaleString()})
                                                            </span>
                                                        </span>
                                                    )
                                                ) : '-'}
                                            </td>
                                            <td className={`whitespace-nowrap px-3 py-4 text-sm text-right ${po.qtyReceived < po.qtyOrdered ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                                                {po.qtyReceived != null ? po.qtyReceived.toLocaleString() : '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                                                {po.qtyDamaged > 0 ? (
                                                    <span className="text-orange-600 font-medium">
                                                        {po.qtyDamaged.toLocaleString()}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">0</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                                                {po.qtyMissing > 0 ? (
                                                    <span className={`font-medium ${(po.qtyCredited || 0) >= po.qtyMissing ? 'text-green-600' : 'text-red-600'}`}>
                                                        {po.qtyMissing.toLocaleString()}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">0</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {formatDate(po.firstReceivedDate)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {formatDate(po.lastReceivedDate)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                                {formatCurrency(po.poTotal)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                                {formatCurrency(po.invoiceTotal)}
                                            </td>
                                            <td className={`whitespace-nowrap px-3 py-4 text-sm text-right ${
                                                po.variance > 0 ? 'text-red-600' : po.variance < 0 ? 'text-green-600' : 'text-gray-500'
                                            }`}>
                                                {formatCurrency(po.variance)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                {po.priceIssues > 0 ? (
                                                    <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                                        {po.priceIssues} Issues
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                        OK
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                {po.skuIssues > 0 ? (
                                                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                                                        {po.skuIssues} Issues
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                        OK
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                {po.status === 'Matched' ? (
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                        Matched
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                                        Issues
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                {po.reviewed ? (
                                                    <svg className="w-5 h-5 mx-auto text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                ) : po.overCompensated ? (
                                                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800" title="Over-compensated: received both credits and replacements">
                                                        Over-comp
                                                    </span>
                                                ) : (
                                                    <svg className="w-5 h-5 mx-auto text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </td>
                                        </tr>
                                        {/* Invoice rows */}
                                        {po.hasInvoices && expandedPOs[po.poNumber] && po.invoices.map((inv) => (
                                            <tr
                                                key={`inv-${inv.id}`}
                                                className="bg-indigo-50 hover:bg-indigo-100 cursor-pointer"
                                                onClick={() => handlePOClick(po)}
                                            >
                                                <td className="whitespace-nowrap py-3 pl-12 pr-3 text-sm font-medium text-indigo-700">
                                                    <div className="flex items-center">
                                                        <svg className="w-4 h-4 mr-2 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        <span className="inline-flex items-center rounded bg-indigo-200 px-1.5 py-0.5 text-xs font-medium text-indigo-800 mr-2">
                                                            INV
                                                        </span>
                                                        #{inv.invoiceNumber}
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                                                    {formatDate(inv.invoiceDate)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400 text-right">-</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                                                    {inv.qtyShipped != null ? inv.qtyShipped.toLocaleString() : '-'}
                                                    <span className="text-gray-400 ml-1">({inv.itemCount})</span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                                                    {inv.qtyReceived != null ? inv.qtyReceived.toLocaleString() : '-'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400">-</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400">-</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400">-</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400">-</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400 text-right">-</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-indigo-600 font-medium text-right">
                                                    {formatCurrency(inv.totalAmount)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400 text-right">-</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    {inv.priceIssues > 0 ? (
                                                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                                                            {inv.priceIssues}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                            OK
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    {inv.skuIssues > 0 ? (
                                                        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                                            {inv.skuIssues}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                            OK
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    {inv.status === 'Matched' ? (
                                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                            Matched
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                                            Issues
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    {inv.priceIssues === 0 && inv.skuIssues === 0 && inv.qtyShipped != null && inv.qtyReceived != null && inv.qtyShipped === inv.qtyReceived ? (
                                                        <svg className="w-5 h-5 mx-auto text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-5 h-5 mx-auto text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Replacement rows */}
                                        {po.hasReplacements && expandedPOs[po.poNumber] && po.replacements.map((repl) => (
                                            <tr
                                                key={repl.poNumber}
                                                className="bg-blue-50 hover:bg-blue-100 cursor-pointer"
                                                onClick={() => handlePOClick({ poNumber: repl.poNumber })}
                                            >
                                                <td className="whitespace-nowrap py-3 pl-12 pr-3 text-sm font-medium text-blue-600">
                                                    <div className="flex items-center">
                                                        <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                        </svg>
                                                        <span className="inline-flex items-center rounded bg-blue-200 px-1.5 py-0.5 text-xs font-medium text-blue-800 mr-2">
                                                            {repl.suffix}
                                                        </span>
                                                        Replacement
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                                                    {formatDate(repl.invoiceDate)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                                                    {repl.qtyOrdered?.toLocaleString() || '-'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-right">
                                                    {repl.qtyShipped != null ? (
                                                        <span className="text-green-600">
                                                            {repl.qtyShipped.toLocaleString()}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                                <td className={`whitespace-nowrap px-3 py-3 text-sm text-right ${repl.qtyReceived < repl.qtyOrdered ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                                    {repl.qtyReceived != null ? repl.qtyReceived.toLocaleString() : '-'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-right">
                                                    {repl.qtyDamaged > 0 ? (
                                                        <span className="text-orange-600 font-medium">
                                                            {repl.qtyDamaged.toLocaleString()}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400">0</span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-right">
                                                    {repl.qtyMissing > 0 ? (
                                                        <span className={`font-medium ${(repl.qtyCredited || 0) >= repl.qtyMissing ? 'text-green-600' : 'text-red-600'}`}>
                                                            {repl.qtyMissing.toLocaleString()}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400">0</span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400">
                                                    -
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400">
                                                    -
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400 text-right">
                                                    -
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                                                    {formatCurrency(repl.totalAmount)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400 text-right">
                                                    -
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    <span className="text-gray-400">-</span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    <span className="text-gray-400">-</span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    {repl.status === 'Matched' ? (
                                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                            Complete
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                                            Replacement
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    <span className="text-gray-400">-</span>
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Credit rows */}
                                        {po.hasCredits && expandedPOs[po.poNumber] && po.credits.map((credit) => (
                                            <tr
                                                key={`credit-${credit.creditNumber}`}
                                                className="bg-green-50 hover:bg-green-100"
                                            >
                                                <td className="whitespace-nowrap py-3 pl-12 pr-3 text-sm font-medium text-green-700">
                                                    <div className="flex items-center">
                                                        <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                        </svg>
                                                        <span className="inline-flex items-center rounded bg-green-200 px-1.5 py-0.5 text-xs font-medium text-green-800 mr-2">
                                                            CR
                                                        </span>
                                                        Credit #{credit.creditNumber}
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                                                    {formatDate(credit.creditDate)}
                                                </td>
                                                <td colSpan="7" className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                                                    {credit.itemCount} item{credit.itemCount !== 1 ? 's' : ''}
                                                    {credit.invoiceNumber ? ` (Inv #${credit.invoiceNumber})` : ''}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400 text-right">
                                                    -
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-green-600 font-medium text-right">
                                                    -{formatCurrency(credit.totalAmount)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    {/* Price validation */}
                                                    {credit.priceMismatchCount > 0 ? (
                                                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800" title={`${credit.priceMismatchCount} price mismatch`}>
                                                            {credit.priceMismatchCount}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                            OK
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    {/* SKU validation */}
                                                    {credit.skuMismatchCount > 0 ? (
                                                        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800" title={`${credit.skuMismatchCount} SKU mismatch`}>
                                                            {credit.skuMismatchCount}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                            OK
                                                        </span>
                                                    )}
                                                </td>
                                                <td colSpan="3" className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                        Credit
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* PO Detail Modal */}
            {selectedPO && (
                <PODetailModal
                    open={detailModalOpen}
                    onClose={handleCloseModal}
                    poNumber={selectedPO.poNumber}
                />
            )}
        </div>
    );
}
